import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
  className?: string;
  showHome?: boolean;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ 
  items = [],
  className = '',
  showHome = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-generate breadcrumbs from current path if items not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(segment => segment !== '');
    const breadcrumbs: BreadcrumbItem[] = [];

    if (showHome) {
      breadcrumbs.push({
        label: 'Dashboard',
        path: '/dashboard',
        icon: '🏠'
      });
    }

    pathSegments.forEach((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const label = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Don't add if it's the dashboard (already added as home)
      if (path !== '/dashboard') {
        breadcrumbs.push({
          label,
          path: index < pathSegments.length - 1 ? path : undefined // Last item shouldn't be clickable
        });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbs();

  const handleItemClick = (item: BreadcrumbItem) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <nav 
      className={`flex items-center space-x-2 text-sm ${className}`}
      aria-label="Breadcrumb"
    >
      <ol className="flex items-center space-x-2">
        {breadcrumbItems.map((item, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <span className="mx-2 text-gray-400">/</span>
            )}
            
            {item.path ? (
              <button
                onClick={() => handleItemClick(item)}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
              >
                {item.icon && (
                  <span className="text-sm">{item.icon}</span>
                )}
                <span>{item.label}</span>
              </button>
            ) : (
              <span className="flex items-center space-x-1 text-gray-700 font-medium">
                {item.icon && (
                  <span className="text-sm">{item.icon}</span>
                )}
                <span>{item.label}</span>
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};